import { type PointerEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import type { HistogramResponse } from '@/api/client';
import { clockIn, compactCount, formatCount, shortStamp, type Zone } from '@/lib/format';

const LEVELS = ['info', 'debug', 'trace', 'warn', 'error', 'fatal'] as const;
const RIGHT = 46;
const AXIS = 20;
// The lane of errors and warnings under the volume, on a scale of its own: one error shows.
const LANE = 22;
const LANE_GAP = 10;
// How long bars take to reach their new height.
const GROW_MS = 520;
const INK_GHOST = '#6a7c9f';

interface Bar {
    t: number;
    counts: Record<string, number>;
}

interface Layout {
    bars: Bar[];
    width: number;
    start: number;
    step: number;
    band: number;
}

export interface HistogramProps {
    data: HistogramResponse | undefined;
    from: number;
    to: number;
    zone: Zone;
    // Following live: the newest bar is the signal's.
    live?: boolean;
    // Request dumps hidden from the list are still counted here: the tooltip says so.
    requestsHidden: boolean;
    // Drag across to zoom in, click a bar to open it; none while the range follows live.
    onZoom: ((from: number, to: number) => void) | null;
}

const sum = (counts: Record<string, number>, levels: readonly string[]): number => levels.reduce((total, level) => total + (counts[level] ?? 0), 0);

// A bar with a rounded top, square at its baseline.
function bar(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    if (h <= 0) {
        return;
    }
    const radius = Math.min(r, w / 2, h);
    g.beginPath();
    if (typeof g.roundRect === 'function') {
        g.roundRect(x, y, w, h, [radius, radius, 0, 0]);
    } else {
        g.rect(x, y, w, h);
    }
    g.fill();
}

// Lines over time: the volume as thin blue bars, errors and warnings on their own lane below; drag to zoom, a bar's
// tooltip says what it holds.
export function Histogram(props: HistogramProps): ReactNode {
    const canvas = useRef<HTMLCanvasElement>(null);
    const [hover, setHover] = useState<{ x: number; index: number; bar: Bar; width: number; band: number } | null>(null);
    const [drag, setDrag] = useState<{ x0: number; x1: number; t0: number; t1: number } | null>(null);
    const layout = useRef<Layout>({ bars: [], width: 0, start: 0, step: 60_000, band: 1 });
    const { zone } = props;
    const live = props.live ?? false;

    // What each bar showed when the canvas was last drawn, by its time, and the scales it was drawn at: a new range grows
    // its bars from nothing, live moves them a little, an interrupted move resumes from where it was.
    const drawn = useRef<{ bars: Map<number, Record<string, number>>; max: number; maxLane: number }>({ bars: new Map(), max: 1, maxLane: 1 });

    useEffect(() => {
        const element = canvas.current;
        if (!element) {
            return;
        }
        const step = (props.data?.stepSeconds ?? 60) * 1000;
        const start = Math.floor(props.from / step) * step;
        const count = Math.max(1, Math.ceil((props.to - start) / step));
        const byTime = new Map((props.data?.points ?? []).map((point) => [point.t, point.counts]));
        const bars: Bar[] = Array.from({ length: count }, (_, i) => ({ t: start + i * step, counts: byTime.get(start + i * step) ?? {} }));
        const target = Math.max(1, ...bars.map((item) => sum(item.counts, LEVELS)));
        const targetLane = Math.max(1, ...bars.map((item) => sum(item.counts, ['warn', 'error', 'fatal'])));
        const before = drawn.current;
        // Between what was drawn and the new counts, `p` of the way (0 to 1, eased).
        const between = (item: Bar, p: number): Record<string, number> => {
            if (p >= 1) {
                return item.counts;
            }
            const was = before.bars.get(item.t) ?? {};
            const out: Record<string, number> = {};
            for (const level of LEVELS) {
                const a = was[level] ?? 0;
                const b = item.counts[level] ?? 0;
                if (a || b) {
                    out[level] = a + (b - a) * p;
                }
            }

            return out;
        };
        let progress = matchMedia('(prefers-reduced-motion: reduce)').matches ? 1 : 0;

        const draw = (): void => {
            const dpr = window.devicePixelRatio || 1;
            const w = element.clientWidth;
            const h = element.clientHeight;
            element.width = w * dpr;
            element.height = h * dpr;
            const g = element.getContext('2d');
            if (!g) {
                return;
            }
            g.scale(dpr, dpr);
            g.clearRect(0, 0, w, h);
            const eased = 1 - (1 - progress) ** 3;
            const shown = bars.map((item) => between(item, eased));
            const max = before.max + (target - before.max) * eased;
            const maxLane = before.maxLane + (targetLane - before.maxLane) * eased;
            const laneBase = h - AXIS;
            const laneTop = laneBase - LANE;
            const base = laneTop - LANE_GAP;
            const top = 10;
            const plot = w - RIGHT;
            const band = plot / count;
            const width = band >= 4 ? Math.max(2, Math.min(12, band * 0.56)) : Math.max(1, band - 1);
            layout.current = { bars, width: w, start, step, band };
            drawn.current = { bars: new Map(bars.map((item, i) => [item.t, shown[i] ?? {}])), max, maxLane };

            g.font = '500 11px "Geist Mono", ui-monospace, monospace';
            g.fillStyle = INK_GHOST;
            g.strokeStyle = 'rgba(109, 167, 236, 0.1)';
            g.lineWidth = 1;
            for (const y of [top, base]) {
                g.beginPath();
                g.moveTo(0, Math.round(y) + 0.5);
                g.lineTo(plot, Math.round(y) + 0.5);
                g.stroke();
            }
            g.textAlign = 'left';
            g.fillText(compactCount(Math.round(target)), plot + 10, top + 4);
            g.fillText('0', plot + 10, base + 3);
            g.fillText(compactCount(Math.round(targetLane)), plot + 10, laneTop + 9);

            shown.forEach((counts, i) => {
                const x = i * band + (band - width) / 2;
                const volume = sum(counts, LEVELS);
                const height = volume > 0 ? Math.max(eased < 1 ? 0 : 1.5, (volume / max) * (base - top)) : 0;
                if (height > 0) {
                    const newest = live && i === count - 1;
                    const fill = g.createLinearGradient(0, base - height, 0, base);
                    fill.addColorStop(0, newest ? 'rgba(242, 232, 48, 0.95)' : 'rgba(108, 164, 247, 0.95)');
                    fill.addColorStop(1, newest ? 'rgba(242, 232, 48, 0.22)' : 'rgba(32, 98, 242, 0.2)');
                    g.fillStyle = fill;
                    bar(g, x, base - height, width, height, 3);
                }
                // The lane: warnings at its foot, errors above them, 1 px apart.
                const warn = counts.warn ?? 0;
                const errors = (counts.error ?? 0) + (counts.fatal ?? 0);
                let y = laneBase;
                if (warn > 0) {
                    const hw = Math.max(2, (warn / maxLane) * LANE);
                    g.fillStyle = '#fab219';
                    bar(g, x, y - hw, width, hw, errors > 0 ? 0 : 2);
                    y -= hw + 1;
                }
                if (errors > 0) {
                    const he = Math.max(2, (errors / maxLane) * LANE);
                    g.fillStyle = '#e5484d';
                    bar(g, x, y - he, width, he, 2);
                }
            });

            g.fillStyle = INK_GHOST;
            g.textAlign = 'center';
            const longRange = props.to - props.from > 24 * 3600_000;
            const labels = longRange ? 4 : 6;
            for (let i = 0; i <= labels; i++) {
                const t = props.from + ((props.to - props.from) * i) / labels;
                const text = longRange ? shortStamp(t, zone) : clockIn(new Date(t), zone).slice(0, 5);
                const half = g.measureText(text).width / 2 + 2;
                g.fillText(text, Math.min(plot - half, Math.max(half, ((t - start) / step) * band)), h - 4);
            }
        };
        let frame = 0;
        const began = performance.now();
        const tick = (now: number): void => {
            progress = Math.min(1, (now - began) / GROW_MS);
            draw();
            if (progress < 1) {
                frame = requestAnimationFrame(tick);
            }
        };
        if (progress < 1) {
            frame = requestAnimationFrame(tick);
        }
        // A tab in the background gets no frames: the bars still end where they belong.
        const settle = setTimeout(() => {
            if (progress < 1) {
                progress = 1;
                draw();
            }
        }, GROW_MS + 80);
        draw();
        const observer = new ResizeObserver(draw);
        observer.observe(element);
        // The canvas draws text: once the data's typeface has loaded, again.
        void document.fonts?.ready.then(draw);

        return () => {
            cancelAnimationFrame(frame);
            clearTimeout(settle);
            observer.disconnect();
        };
    }, [props.data, props.from, props.to, zone, live]);

    const xOf = (event: PointerEvent): number => {
        const rect = canvas.current?.getBoundingClientRect();

        return rect ? Math.max(0, Math.min(rect.width - RIGHT, event.clientX - rect.left)) : 0;
    };
    const timeAt = (x: number): number => {
        const { start, step, band } = layout.current;

        return Math.max(props.from, Math.min(props.to, start + (x / band) * step));
    };
    const indexAt = (x: number): number => Math.floor(x / layout.current.band);

    const counts = hover?.bar.counts ?? {};
    const total = sum(counts, LEVELS);
    const errors = (counts.error ?? 0) + (counts.fatal ?? 0);
    const warnings = counts.warn ?? 0;

    return (
        <div className={`hist-body ${props.onZoom ? 'zoomable' : ''}`}>
            {hover && !drag && <div className="hcol" style={{ left: hover.index * hover.band, width: Math.max(8, hover.band) }} />}
            <canvas
                ref={canvas}
                // The pointer is captured on press: the drag follows it off the canvas and ends wherever it is let go.
                onPointerDown={(event) => {
                    if (event.button === 0 && props.onZoom) {
                        event.currentTarget.setPointerCapture(event.pointerId);
                        const x = xOf(event);
                        const t = timeAt(x);
                        setDrag({ x0: x, x1: x, t0: t, t1: t });
                        setHover(null);
                    }
                }}
                onPointerMove={(event) => {
                    const x = xOf(event);
                    if (drag) {
                        setDrag({ ...drag, x1: x, t1: timeAt(x) });

                        return;
                    }
                    const index = indexAt(x);
                    const item = layout.current.bars[index];
                    setHover(item ? { x, index, bar: item, width: event.currentTarget.clientWidth, band: layout.current.band } : null);
                }}
                onPointerUp={(event) => {
                    if (!drag || !props.onZoom) {
                        return;
                    }
                    const x1 = xOf(event);
                    setDrag(null);
                    if (Math.abs(x1 - drag.x0) > 4) {
                        props.onZoom(Math.round(timeAt(Math.min(drag.x0, x1))), Math.round(timeAt(Math.max(drag.x0, x1))));
                    } else {
                        const item = layout.current.bars[indexAt(drag.x0)];
                        if (item) {
                            props.onZoom(item.t, item.t + layout.current.step);
                        }
                    }
                }}
                onPointerCancel={() => setDrag(null)}
                onPointerLeave={() => setHover(null)}
            />
            {drag && Math.abs(drag.x1 - drag.x0) > 1 && (
                <div className="brush" style={{ left: Math.min(drag.x0, drag.x1), width: Math.abs(drag.x1 - drag.x0) }}>
                    <span>
                        {clockIn(new Date(Math.min(drag.t0, drag.t1)), zone)} → {clockIn(new Date(Math.max(drag.t0, drag.t1)), zone)}
                    </span>
                </div>
            )}
            {hover && !drag && (
                <div className="tip" style={{ left: Math.min(hover.x + 18, Math.max(0, hover.width - 300)), top: -6 }}>
                    <b>{clockIn(new Date(hover.bar.t), zone)}</b>
                    {total === 0
                        ? ' · nothing'
                        : ` · ${formatCount(total)} ${total === 1 ? 'line' : 'lines'}${errors ? ` · ${formatCount(errors)} ${errors === 1 ? 'error' : 'errors'}` : ''}${warnings ? ` · ${formatCount(warnings)} ${warnings === 1 ? 'warning' : 'warnings'}` : ''}`}
                    {props.requestsHidden && total > 0 ? ', request dumps included' : ''}
                    {props.onZoom && <div className="tip-hint">Click to open this bar · drag to zoom</div>}
                </div>
            )}
        </div>
    );
}
